import { useMutation } from "@tanstack/react-query";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { toast } from "react-toastify";

import { updateAiSettings } from "api/recommendation-summary/recommendation-summary";
import { AiSettings } from "api/recommendation-summary/types";
import { useAuthContext } from "providers/Auth";
import { getErrorMessage } from "utils/error";

// PostHog flag that gates the whole AI recommendation summary rollout
// (modal card + Settings → AI page). See BSCPF-6408.
export const AI_RECOMMENDATION_SUMMARY_FLAG = "ai-recommendation-summary";

const AI_SETTINGS_MUTATION_KEY = ["AI_SETTINGS", "UPDATE"];

/**
 * Single source of truth for whether the AI recommendation summary should be
 * shown: the PostHog rollout flag AND the user's personal preference, which
 * lives on the auth user (`ai_settings.explanation_summary`).
 */
export const useAiRecommendationSummary = () => {
  const { auth, patchAuth } = useAuthContext();
  // `undefined` until PostHog has resolved the flags.
  const flag = useFeatureFlagEnabled(AI_RECOMMENDATION_SUMMARY_FLAG);
  const isFlagEnabled = Boolean(flag);

  const settings: AiSettings | undefined = auth?.ai_settings;

  const update = useMutation({
    mutationKey: AI_SETTINGS_MUTATION_KEY,
    mutationFn: (payload: Partial<AiSettings>) => updateAiSettings(payload),
    onSuccess: (ai_settings) => {
      patchAuth({ ai_settings });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  return {
    flag,
    isFlagEnabled,
    isSummaryEnabled: isFlagEnabled && Boolean(settings?.explanation_summary),
    settings,
    update
  };
};
